import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { formatDate, todayIso } from '../dates';
import { isActiveEmployee } from '../employeeDirectory';
import { benefitsGateway } from '../index';
import { loadAllPlans } from '../loaders';
import { payrollEffect } from '../planSummary';
import { useEmployeeDirectory } from '../useEmployeeDirectory';
import {
  ENROLLMENT_METHODS, coverageLevelLabels, enrollmentMethodLabels, type BenefitElection, type BenefitPlan, type CoverageLevel, type EnrollmentMethod,
} from '../types';
import { COVERAGE_LEVEL_CHOICES } from '../planForm';
import { SelectInput, TextAreaInput, TextInput } from './fields';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

const MAX_REASON = 200;

export interface EnrollmentDialogProps {
  initialPlanId?: string;
  onClose: () => void;
  /** An election was saved: the page reloads its lists. */
  onChanged: (message: string) => void;
  onForbidden: () => void;
}

/**
 * Manage Enrollment: enroll an employee in a plan or record that they waived it. Effective-dated, with an optional qualifying-life-event reason and a
 * payroll-effect preview (fixed amounts only; a percentage says "calculated at payroll"). Nothing here decides eligibility (Phase 1 eligibility is active
 * employees) and nothing enrolls anyone with an insurance carrier. The server is the authority: a plan that needs tax review, is inactive, or is not
 * in effect on the start date refuses the election, and the refusal is shown in plain words.
 */
export function EnrollmentDialog({ initialPlanId, onClose, onChanged, onForbidden }: EnrollmentDialogProps) {
  const { state: directory } = useEmployeeDirectory();
  const [plans, setPlans] = useState<BenefitPlan[] | null>(null);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [planId, setPlanId] = useState(initialPlanId ?? '');
  const [action, setAction] = useState<'enroll' | 'waive'>('enroll');
  const [coverageLevel, setCoverageLevel] = useState<CoverageLevel | ''>('');
  const [start, setStart] = useState(todayIso());
  const [source, setSource] = useState<EnrollmentMethod>('AdminEnrolled');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [current, setCurrent] = useState<BenefitElection[]>([]);

  useEffect(() => {
    let cancelled = false;
    loadAllPlans()
      .then((list) => { if (!cancelled) setPlans(list.filter((p) => p.isActive)); })
      .catch((err: unknown) => { if (!cancelled) { if (isForbidden(err)) onForbidden(); setPlansError(bdErrorMessage(err, 'Could not load the plans.')); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The chosen employee's elections, so the person sees what they are about to replace.
  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    benefitsGateway.listEmployeeElections(employeeId).then((list) => { if (!cancelled) setCurrent(list); }).catch(() => { if (!cancelled) setCurrent([]); });
    return () => { cancelled = true; };
  }, [employeeId, done]);

  const employees = useMemo(() => {
    if (directory.kind !== 'ready') return [];
    const q = employeeFilter.trim().toLowerCase();
    return directory.employees.filter(isActiveEmployee).filter((e) => !q || e.name.toLowerCase().includes(q) || e.employeeNumber.toLowerCase().includes(q));
  }, [directory, employeeFilter]);

  const plan = plans?.find((p) => p.id === planId) ?? null;
  const tiered = plan?.contributionMethod === 'TieredByCoverage';
  const coverageChoices: CoverageLevel[] = tiered ? plan!.coverageTiers.map((t) => t.coverageLevel) : COVERAGE_LEVEL_CHOICES;
  const effectiveCoverage: CoverageLevel | null = action === 'waive' ? null : coverageLevel === '' ? null : coverageLevel;
  const needsReview = plan !== null && plan.configurationStatus !== 'Complete';
  const outsideWindow = plan !== null && start !== '' && ((plan.effectiveStartDate !== null && start < plan.effectiveStartDate) || (plan.effectiveEndDate !== null && start > plan.effectiveEndDate));
  const open = current.filter((e) => e.benefitCodeId === planId && (e.effectiveEndDate === null || e.effectiveEndDate >= todayIso()));

  function changePlan(next: string) {
    setPlanId(next);
    setCoverageLevel('');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const problems: Record<string, string> = {};
    if (!employeeId) problems.employee = 'Choose an employee.';
    if (!planId) problems.plan = 'Choose a plan.';
    if (!start) problems.start = 'Choose a start date.';
    if (action === 'enroll' && tiered && coverageLevel === '') problems.coverage = 'Choose one of the coverage levels this plan offers.';
    if (reason.trim().length > MAX_REASON) problems.reason = `Use ${MAX_REASON} characters or fewer.`;
    setErrors(problems);
    setFormError(null);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    try {
      const waived = action === 'waive';
      await benefitsGateway.createElection(employeeId, {
        benefitCodeId: planId,
        effectiveStartDate: start,
        isWaived: waived,
        coverageLevel: waived ? null : effectiveCoverage,
        qualifyingEventReason: reason.trim() === '' ? null : reason.trim(),
        enrollmentSource: source,
        // Enrolling or waiving here changes the employee's current election of the plan: it replaces it from the start date.
        replaceCurrent: true,
      });
      const who = directory.kind === 'ready' ? directory.employees.find((x) => x.id === employeeId)?.name ?? 'The employee' : 'The employee';
      const message = waived
        ? `A waiver was recorded for ${who} on ${plan?.name ?? 'the plan'}, effective ${formatDate(start)}.`
        : `${who} is enrolled in ${plan?.name ?? 'the plan'}, effective ${formatDate(start)}.`;
      setDone(message);
      onChanged(message);
    } catch (err) {
      if (isForbidden(err)) onForbidden();
      setFormError(bdErrorMessage(err, 'Could not save this election.', 'election'));
    } finally {
      setBusy(false);
    }
  }

  function again() {
    setDone(null);
    setEmployeeId('');
    setEmployeeFilter('');
    setReason('');
    setErrors({});
    setFormError(null);
  }

  const planOptions = [
    { value: '', label: plans === null ? 'Loading plans…' : 'Choose a plan' },
    ...(plans ?? []).map((p) => ({ value: p.id, label: p.configurationStatus === 'Complete' ? p.name : `${p.name} (needs tax review)` })),
  ];
  const employeeOptions = [
    { value: '', label: directory.kind === 'loading' ? 'Loading employees…' : employees.length === 0 ? 'No matching active employees' : 'Choose an employee' },
    ...employees.map((x) => ({ value: x.id, label: `${x.name} (${x.employeeNumber})` })),
  ];
  const effect = plan ? payrollEffect(plan, effectiveCoverage, action === 'waive') : [];

  return (
    <ModalDialog
      title="Manage Enrollment"
      size="lg"
      busy={busy}
      onClose={onClose}
      footer={done ? (
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={again}>Enroll another employee</button>
          <button type="button" className="jr-btn jr-btn--primary" data-autofocus onClick={onClose}>Close</button>
        </>
      ) : (
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form="bd-enroll-form" className="jr-btn jr-btn--primary" disabled={busy || plansError !== null}>
            {busy ? 'Saving…' : action === 'waive' ? 'Record waiver' : 'Enroll employee'}
          </button>
        </>
      )}
    >
      {done ? (
        <p className="jr-notice jr-notice--ok" role="status"><span aria-hidden="true">{'✓'} </span>{done}</p>
      ) : (
        <form id="bd-enroll-form" className="bd-form" onSubmit={(e) => void submit(e)} noValidate>
          <p className="bd-help">
            Phase 1 treats every active employee as eligible. Enrolling here records the election in EPN only: it does not enroll an employee with an insurance carrier.
          </p>
          {plansError && <p className="jr-error jr-error--block" role="alert">{plansError}</p>}
          {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}

          <fieldset className="bd-fieldset">
            <legend>Who and what</legend>
            <div className="jr-grid">
              <TextInput id="bd-enroll-filter" label="Find an employee" type="search" value={employeeFilter} onChange={setEmployeeFilter} hint="Type part of a name or employee number." />
              <SelectInput id="bd-enroll-employee" label="Employee" required value={employeeId} onChange={setEmployeeId} options={employeeOptions} error={errors.employee} />
              <SelectInput id="bd-enroll-plan" label="Plan" required value={planId} onChange={changePlan} options={planOptions} error={errors.plan} />
              <TextInput id="bd-enroll-start" label="Effective date" type="date" required value={start} onChange={setStart} error={errors.start} help="The day the election starts. A plan can only take elections between its own start and end dates." />
            </div>
            <div className="bd-notice" role="group" aria-label="Choice">
              <label className="bd-check" htmlFor="bd-enroll-action-enroll">
                <input id="bd-enroll-action-enroll" type="radio" name="bd-enroll-action" checked={action === 'enroll'} onChange={() => setAction('enroll')} />
                Enroll the employee
              </label>
              <label className="bd-check" htmlFor="bd-enroll-action-waive">
                <input id="bd-enroll-action-waive" type="radio" name="bd-enroll-action" checked={action === 'waive'} onChange={() => setAction('waive')} />
                Record a waiver (the employee declined)
              </label>
            </div>
          </fieldset>

          {needsReview && (
            <p className="bd-notice bd-notice--warn" role="status">
              This plan is "Configuration incomplete: tax review required". An employee cannot be enrolled in it yet; you can still record a waiver.
              {plan!.configurationIssues.length > 0 && ` ${plan!.configurationIssues[0]}`}
            </p>
          )}
          {outsideWindow && (
            <p className="bd-notice bd-notice--warn" role="status">
              The start date is outside this plan's effective dates ({plan!.effectiveStartDate ? formatDate(plan!.effectiveStartDate) : 'no start'} to {plan!.effectiveEndDate ? formatDate(plan!.effectiveEndDate) : 'no end'}).
            </p>
          )}

          {action === 'enroll' && (
            <fieldset className="bd-fieldset">
              <legend>Coverage</legend>
              <div className="jr-grid">
                <SelectInput
                  id="bd-enroll-coverage" label="Coverage level" required={tiered} value={coverageLevel} onChange={(v) => setCoverageLevel(v as CoverageLevel | '')} error={errors.coverage}
                  options={[{ value: '', label: tiered ? 'Choose a coverage level' : 'Not specified' }, ...coverageChoices.map((c) => ({ value: c, label: coverageLevelLabels[c] }))]}
                  help="Who the coverage is for. A plan with an amount for each coverage level only offers its own levels."
                />
                <SelectInput
                  id="bd-enroll-source" label="How the election was made" value={source} onChange={(v) => setSource(v as EnrollmentMethod)}
                  options={ENROLLMENT_METHODS.map((m) => ({ value: m, label: enrollmentMethodLabels[m] }))}
                />
              </div>
            </fieldset>
          )}

          <TextAreaInput
            id="bd-enroll-reason" label="Reason or qualifying life event (optional)" value={reason} onChange={setReason} rows={2} maxLength={MAX_REASON} error={errors.reason}
            hint={`Up to ${MAX_REASON} characters, for example "New hire" or "Marriage". Do not enter health information.`}
          />

          {plan && (
            <div className="bd-panel" aria-live="polite" data-testid="payroll-effect">
              <h3>What this does to pay</h3>
              <ul className="bd-list">{effect.map((line) => <li key={line}>{line}</li>)}</ul>
              {open.length > 0 && action === 'enroll' && (
                <p className="bd-help">
                  This employee already has an election for this plan from {formatDate(open[0]!.effectiveStartDate)}. Saving ends it on the new start date and keeps it in the history.
                </p>
              )}
            </div>
          )}
        </form>
      )}
    </ModalDialog>
  );
}
